import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import icons from "../../../assets/images/constantIcons";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";

const EditTableForm = ({ onClose, editPopup, tableId, fetchTabledata }) => {
  const [tableData, setTableData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);


  // fetch Table by id
  useEffect(() => {
    const fetchTabledataId = async () => {
      try {
        const response = await apiService.get(`tables/${tableId}`);
        if (response.data) {
          const table = response.data;
          const [day_start, day_end] = table.day_time_range?.split(" - ") || ["", ""];
          const [night_start, night_end] = table.night_time_range?.split(" - ") || ["", ""];

          setTableData({
            name: table.name || "",
            day_charge: table.day_charge || "",
            night_charge: table.night_charge || "",
            day_start,
            day_end,
            night_start,
            night_end,
          });
        }
      } catch (errors) {
        handleError(errors);
      }
    };

    if (editPopup && tableId) {
      fetchTabledataId();
    }
  }, [editPopup, tableId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: tableData || {
      name: "",
      day_charge: "",
      night_charge: "",
      day_start: "",
      day_end: "",
      night_start: "",
      night_end: "",
    },

    validationSchema: Yup.object({
      name: Yup.string().required("Table name is required"),
  day_charge: Yup.number()
        .typeError("Must be a number")
        .required("Day charge is required")
        .moreThan(0, "Charge must be greater than 0"),
      
      night_charge: Yup.number()
        .typeError("Must be a number")
        .required("Night charge is required")
        .moreThan(0, "Charge must be greater than 0"),
      day_start: Yup.string().required("Day start time is required"),
      day_end: Yup.string()
        .required("Day end time is required")
        .test("is-after-day-start", "Day end time must be after day start", function (value) {
          return value > this.parent.day_start;
        }),
      night_start: Yup.string()
        .required("Night start time is required"),
      night_end: Yup.string()
        .required("Night end time is required")
    }),

    onSubmit: async (values) => {
      const day_time_range = `${values.day_start} - ${values.day_end}`;
      const night_time_range = `${values.night_start} - ${values.night_end}`;

      const updatedTable = {
        name: values.name,
        day_charge: parseFloat(values.day_charge),
        night_charge: parseFloat(values.night_charge),
        day_time_range,
        night_time_range,
      };

      setIsSubmitting(true);

      try {
        const response = await apiService.put(`tables/${tableId}`, updatedTable);
        onClose();
        toast.success(response.message, { duration: 2000 });
        fetchTabledata(); // Fetch updated data
      } catch (errors) {
        handleError(errors);
      } finally {
        setIsSubmitting(false);
      }
    },
  });



  return (
    editPopup && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] s:w-[350px] md:w-[460px] shadow-lg relative">
          {/* Close Button */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Edit Table</h2>
            <div
              className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
              onClick={onClose}
            >
              <icons.IoClose className="text-green700" />
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={formik.handleSubmit}
            className="space-y-4 text-start h-[70vh] overflow-y-auto vertical"
          >
            {/* Table Name */}
            <div className="text-G300 text-start">
              <label className="block text-sm font-normal mb-2">
                Table Name
              </label>
              <input
                type="text"
                name="name"
                placeholder="Table name"
                className="inputtext "
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-red text-sm">{formik.errors.name}</div>
              )}
            </div>

            {/* Day Hourly Charge */}
            <div className="text-G300 text-start">
              <label className="block text-sm font-normal mb-2">
                Day Charge (per minutes)
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                <input
                  type="text"
                  name="day_charge"
                  step="0.01" 
                  placeholder="Day hourly charge"
                  className="inputtext text-green placeholder:text-green"
                  style={{ paddingLeft: "25px" }}
                  value={formik.values.day_charge}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "0") {
                      formik.setFieldValue("day_charge", ""); // Prevent 0 input
                    } else {
                      formik.handleChange(e);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "-" ||
                      (e.key === "0" && !formik.values.day_charge)
                    ) {
                      e.preventDefault(); // Block negative & leading 0
                    }
                  }}
                  min="1" // Prevents values below 1
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.touched.day_charge && formik.errors.day_charge && (
                <div className="text-red text-sm">
                  {formik.errors.day_charge}
                </div>
              )}
            </div>

            {/* Night Hourly Charge */}
            <div className="text-G300 text-start">
              <label className="block text-sm font-normal mb-2">
                Night Charge (per minutes)
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                <input
                  type="text"
                  name="night_charge"
                  step="0.01" 
                  placeholder="Night hourly charge"
                  className="inputtext text-green placeholder:text-green"
                  style={{ paddingLeft: "25px" }}
                  value={formik.values.night_charge}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "0") {
                      formik.setFieldValue("night_charge", ""); // Prevent 0 input
                    } else {
                      formik.handleChange(e);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "-" ||
                      (e.key === "0" && !formik.values.night_charge)
                    ) {
                      e.preventDefault(); // Block negative & leading 0
                    }
                  }}
                  min="1" // Prevents values below 1
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.touched.night_charge && formik.errors.night_charge && (
                <div className="text-red text-sm">
                  {formik.errors.night_charge}
                </div>
              )}
            </div>

            <div className="text-G300">
              {/* Day Time Range */}
              <label className="block text-sm font-normal mb-2">
                Day Time Range
              </label>
              <div className="flex gap-2">
                <div
                  className="w-full flex flex-col  mt-[5px] h-[40px] border-none border-[1px] rounded"
                  onClick={() =>
                    document.querySelector("#day_start").showPicker()
                  }
                >
                  <input
                    type="time"
                    className="inputtext custom-date-input"
                    id="day_start"
                    name="day_start"
                    placeholder="HH:MM"
                    value={formik.values.day_start}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("day_start", value);
                      formik.setFieldValue("night_end", value); // set night start time in default

                      formik.validateField("day_start");
                      formik.validateField("night_end");
                    }}
                  />
                  {formik.touched.day_start && formik.errors.day_start && (
                    <div className="text-red text-sm">
                      {formik.errors.day_start}
                    </div>
                  )}
                </div>
                <span className="text-sm self-center">to</span>
                <div
                  className="w-full flex flex-col mt-[5px] h-[40px] border-none border-[1px] rounded"
                  onClick={() =>
                    document.querySelector("#day_end").showPicker()
                  }
                >
                  <input
                    type="time"
                    className="inputtext custom-date-input"
                    id="day_end"
                    name="day_end"
                    placeholder="HH:MM"
                    value={formik.values.day_end}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("day_end", value);
                      formik.setFieldValue("night_start", value); // set night end time in default

                      formik.validateField("day_end");
                      formik.validateField("night_start");
                    }}
                  />
                  {formik.touched.day_end && formik.errors.day_end && (
                    <div className="text-red text-sm">
                      {formik.errors.day_end}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="text-G300 mt-4">
              {/* Night Time Range */}
              <label className="block text-sm font-normal mb-2">
                Night Time Range
              </label>
              <div className="flex gap-2">
                <div
                  className="w-full flex flex-col mt-[5px] h-[40px] border-none border-[1px] rounded"
                  onClick={() =>
                    document.querySelector("#night_start").showPicker()
                  }
                >
                  <input
                    type="time"
                    className="inputtext custom-date-input cursor-not-allowed"
                    id="night_start"
                    name="night_start"
                    placeholder="HH:MM"
                    value={formik.values.night_start}
                    onChange={(e) =>
                      formik.setFieldValue("night_start", e.target.value)
                    }
                    disabled
                  />
                  {formik.touched.night_start && formik.errors.night_start && (
                    <div className="text-red text-sm">
                      {formik.errors.night_start}
                    </div>
                  )}
                </div>
                <span className="text-sm self-center">to</span>
                <div
                  className="w-full flex flex-col mt-[5px] h-[40px] border-none border-[1px] rounded"
                  onClick={() =>
                    document.querySelector("#night_end").showPicker()
                  }
                >
                  <input
                    type="time"
                    className="inputtext custom-date-input  cursor-not-allowed"
                    id="night_end"
                    name="night_end"
                    placeholder="HH:MM"
                    value={formik.values.night_end}
                    onChange={(e) =>
                      formik.setFieldValue("night_end", e.target.value)
                    }
                    disabled
                  />
                  {formik.touched.night_end && formik.errors.night_end && (
                    <div className="text-red text-sm">
                      {formik.errors.night_end}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-4 pt-8">
              <button
                type="button"
                onClick={onClose}
                className="w-[75px] sm:w-[93px] text-sm text-center py-2 bg-white text-green rounded-lg border border-green"
              >
                Close
              </button>
              <button
                type="submit"
                className={`w-[75px] sm:w-[93px] text-sm text-center py-2 rounded-lg ${isSubmitting
                  ? "bg-green50 cursor-not-allowed"
                  : "bg-green text-white"
                  }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

export default EditTableForm;
